import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  esInteligente, useBorrarRegistro, useGuardarRegistro, useRegistro, type Campo,
} from './api';
import { BuscadorDeEmpleado, ValorDeCampo } from './piezas';

/**
 * FormularioDeRegistro — una fila de un recurso, con sus columnas dinámicas.
 *
 * ## Lo que cambia
 *
 * - **Eliminar ya no cierra el formulario para preguntar.** `Utils.confirm`
 *   reutiliza el mismo `#modal-overlay` que estaba abierto: se veía la pregunta
 *   y debajo ya no estaba el registro. Aquí la confirmación se apila.
 * - **El autocompletar de empleados no deja escuchas sueltas.** El vanilla
 *   añadía un `click` a `document` por cada apertura del formulario y no lo
 *   quitaba nunca.
 * - **Los campos calculados se ven al crear, no solo al editar.** Antes el
 *   bloque «automáticos» solo aparecía con `eid`, así que al crear no había
 *   forma de saber que esas columnas existían.
 */
export function FormularioDeRegistro({
  id, recursoId, campos, esSistema, pideColaborador, colaboradorFijo, onCerrar,
}: {
  id: number | null;
  recursoId: number;
  campos: Campo[];
  esSistema: boolean;
  /** Sub-dominio del expediente abierto desde la rejilla: hay que preguntar de
   *  quién es la fila, porque el servidor la exige. */
  pideColaborador: boolean;
  /** Entrando desde el expediente ya se sabe de quién es. */
  colaboradorFijo?: number;
  onCerrar: () => void;
}) {
  const consulta = useRegistro(id);
  const guardar = useGuardarRegistro();
  const borrar = useBorrarRegistro();

  const [valores, setValores] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  const [colaborador, setColaborador] = useState<number | null>(colaboradorFijo ?? null);
  const [nombreColaborador, setNombreColaborador] = useState('');
  const [error, setError] = useState('');
  const [borrando, setBorrando] = useState(false);

  const entrada = consulta.data;
  const editables = useMemo(() => campos.filter((f) => !esInteligente(f.type)), [campos]);
  const calculados = useMemo(() => campos.filter((f) => esInteligente(f.type)), [campos]);
  const preguntaColaborador = pideColaborador && colaboradorFijo == null;

  useEffect(() => {
    if (!entrada) return;
    const datos = entrada.data ?? {};
    const iniciales: Record<string, string> = {};
    for (const [k, v] of Object.entries(datos)) iniciales[k] = v == null ? '' : String(v);
    setValores(iniciales);
    if (entrada.subject_user_id) setColaborador(entrada.subject_user_id);
    setNombreColaborador(entrada.subject_name ?? '');
  }, [entrada]);

  function poner(clave: string, valor: string) {
    setValores((v) => ({ ...v, [clave]: valor }));
    setError('');
  }

  function enviar() {
    const faltan = editables.filter(
      (f) => f.required && !f.readonly && !(valores[f.key] ?? '').trim());
    if (faltan.length) {
      setError(`Falta ${faltan.length === 1 ? 'el campo' : 'los campos'}: ${faltan.map((f) => f.label).join(', ')}.`);
      return;
    }
    if (preguntaColaborador && !colaborador) {
      setError('Elige de qué colaborador es este registro.');
      return;
    }

    const datos: Record<string, string> = {};
    for (const f of editables) {
      if (f.readonly) continue;
      datos[f.key] = (valores[f.key] ?? '').trim();
      // El id resuelto del autocompletar viaja aparte: sin él, el servidor
      // tiene que adivinar la persona por su nombre.
      const idElegido = valores[`${f.key}_id`];
      if (f.type === 'employee' && idElegido) datos[`${f.key}_id`] = idElegido;
    }

    guardar.mutate(
      {
        id,
        recursoId,
        cuerpo: {
          data: datos,
          reason: motivo.trim() || null,
          ...(colaborador ? { subject_user_id: colaborador } : {}),
        },
      },
      {
        onSuccess: (r) => {
          Utils.toast(id ? 'Registro actualizado' : 'Registro creado', 'success');
          // Base de Datos Empleados: lo que no se pudo reflejar en el usuario
          // real —una cédula o un correo que ya tiene otro— se avisa.
          if (r?.skipped) Object.values(r.skipped).forEach((m) => Utils.toast(String(m), 'warning'));
          onCerrar();
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'No se pudo guardar'),
      },
    );
  }

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v && !guardar.isPending) onCerrar(); }}
      titulo={id ? 'Editar registro' : 'Nuevo registro'}
      descripcion={id ? 'Cada cambio crea una versión nueva, con su motivo.' : undefined}
      pie={
        <>
          {id != null && !esSistema && (
            <Button variant="ghost" className="mr-auto text-danger-ink" onClick={() => setBorrando(true)}>
              <i className="bi bi-trash" aria-hidden="true" /> Eliminar
            </Button>
          )}
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={guardar.isPending}>
            <i className="bi bi-check-lg" aria-hidden="true" /> Guardar
          </Button>
        </>
      }
    >
      {id != null && consulta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : id != null && consulta.isError ? (
        <EstadoError err={consulta.error} que="el registro" onReintentar={() => void consulta.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          {preguntaColaborador && (
            <Field
              label="Colaborador"
              ayuda="Este registro va al expediente de esa persona."
              error={error && !colaborador ? error : undefined}
              id="ef-colaborador"
            >
              <BuscadorDeEmpleado
                id="ef-colaborador"
                etiqueta="Buscar colaborador por su nombre"
                valorInicial={nombreColaborador}
                alElegir={(e) => { setColaborador(e.id); setError(''); }}
                // Escribir a mano invalida la elección anterior: si no, se
                // guardaría un nombre editado con el id de otra persona.
                alEscribir={() => setColaborador(null)}
              />
            </Field>
          )}

          {editables.map((f) => (
            <CampoDeRegistro
              key={f.key}
              campo={f}
              valor={valores[f.key] ?? ''}
              onCambio={(v) => poner(f.key, v)}
              onElegirEmpleado={(idEmpleado) => poner(`${f.key}_id`, idEmpleado)}
              error={error && f.required && !(valores[f.key] ?? '').trim() ? error : undefined}
            />
          ))}

          {calculados.length > 0 && (
            <section className="rounded-sm border border-line bg-sunken p-3">
              <h3 className="mb-1.5 flex items-center gap-2 text-2 font-bold text-brand-ink">
                <i className="bi bi-magic" aria-hidden="true" /> Campos calculados
              </h3>
              <ul className="flex flex-col gap-1">
                {calculados.map((f) => (
                  <li key={f.key} className="flex items-baseline justify-between gap-3 text-2">
                    <span className="text-muted">{f.label}</span>
                    <span className="min-w-0 text-right text-ink">
                      {id ? <ValorDeCampo campo={f} valor={valores[f.key]} />
                        : <span className="text-muted">se calcula al guardar</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {id != null && (
            <Field label="Motivo del cambio" opcional ayuda="Queda escrito en el historial de versiones.">
              <Input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Por qué cambias este registro"
              />
            </Field>
          )}

          {error && !!colaborador && (
            <p role="alert" className="text-2 text-danger-ink">{error}</p>
          )}
        </div>
      )}

      {id != null && (
        <ConfirmDialog
          abierto={borrando}
          onCambio={setBorrando}
          titulo="Eliminar el registro"
          descripcion="El registro deja de listarse y queda constancia en la auditoría. Su historial de versiones se conserva."
          textoConfirmar="Eliminar"
          destructivo
          cargando={borrar.isPending}
          onConfirmar={() => borrar.mutate(id, {
            onSuccess: () => {
              Utils.toast('Registro eliminado', 'success');
              setBorrando(false);
              onCerrar();
            },
            onError: (err) => {
              setBorrando(false);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          })}
        />
      )}
    </Drawer>
  );
}

/** Un campo, del tipo que sea. */
function CampoDeRegistro({
  campo, valor, onCambio, onElegirEmpleado, error,
}: {
  campo: Campo;
  valor: string;
  onCambio: (v: string) => void;
  onElegirEmpleado: (id: string) => void;
  error?: string;
}) {
  const idCampo = `ef-${campo.key}`;

  // Las columnas calculadas del maestro —estado, ¿tiene hijos?, n° de hijos— se
  // re-derivan al guardar: editarlas aquí no tendría ningún efecto.
  if (campo.readonly) {
    return (
      <Field label={<>{campo.label} <span className="font-normal text-muted">(automático)</span></>} id={idCampo}>
        <Input value={valor} disabled readOnly />
      </Field>
    );
  }

  return (
    <Field
      label={campo.label}
      opcional={!campo.required}
      error={error}
      id={idCampo}
    >
      {campo.type === 'textarea' ? (
        <Textarea rows={2} value={valor} onChange={(e) => onCambio(e.target.value)} />
      ) : campo.type === 'select' ? (
        <Select value={valor} onChange={(e) => onCambio(e.target.value)}>
          <option value="">—</option>
          {(campo.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      ) : campo.type === 'employee' ? (
        <BuscadorDeEmpleado
          id={idCampo}
          etiqueta={campo.label}
          valorInicial={valor}
          alElegir={(e) => { onCambio(e.label); onElegirEmpleado(String(e.id)); }}
          alEscribir={(t) => { onCambio(t); onElegirEmpleado(''); }}
        />
      ) : (
        <Input
          type={campo.type === 'number' ? 'number'
            : campo.type === 'date' ? 'date'
              : campo.type === 'email' ? 'email'
                : campo.type === 'phone' ? 'tel' : 'text'}
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
        />
      )}
    </Field>
  );
}
